import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { getDashboardStats, getBalances, getCalendarData, getLeaves, getHolidays, getUpcomingBirthdays } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../i18n/I18nContext';
import { getPrivacyAwareLeaveColor, getPrivacyAwareLeaveTypeName } from '../lib/leave-privacy';
import { formatLeaveValue, getUnitLabel } from '../lib/format-unit';

// Format date as YYYY-MM-DD in local timezone (not UTC)
function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Helper to lighten a hex color
function lightenColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent * 100);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  return `#${(1 << 24 | R << 16 | G << 8 | B).toString(16).slice(1)}`;
}

// Generate gradient from leave type color
function getGradient(color) {
  if (!color) color = '#6B7280';
  const lighterColor = lightenColor(color, 0.25);
  return `linear-gradient(135deg, ${lighterColor} 0%, ${color} 100%)`;
}

// Format hours as "X days Y hours" (assuming 8 hours per day)
function formatHoursAsDaysAndHours(hours, t) {
  if (hours === 0) return `0 ${t('time.hours')}`;

  const HOURS_PER_DAY = 8;
  // Clean up floating-point dust but preserve real fractions (e.g. 1.5h, 7.5h)
  const totalHours = Math.round(hours * 100) / 100;
  const days = Math.floor(totalHours / HOURS_PER_DAY);
  const remainingHours = Math.round((totalHours - days * HOURS_PER_DAY) * 100) / 100;

  const parts = [];
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? t('time.day') : t('time.days')}`);
  }
  if (remainingHours > 0 || parts.length === 0) {
    parts.push(`${remainingHours} ${remainingHours === 1 ? t('time.hour') : t('time.hours')}`);
  }

  return parts.join(' ');
}

// Compact format for gauge display: "26d 4h" or just "26d" / "4h"
function formatHoursCompact(hours) {
  if (hours === 0) return { days: 0, hours: 0 };

  const HOURS_PER_DAY = 8;
  // Clean up floating-point dust but preserve real fractions (e.g. 1.5h, 7.5h)
  const totalHours = Math.round(hours * 100) / 100;
  const days = Math.floor(totalHours / HOURS_PER_DAY);
  const remainingHours = Math.round((totalHours - days * HOURS_PER_DAY) * 100) / 100;

  return { days, hours: remainingHours };
}

// Format date for display with locale support
function formatDateShort(dateStr, locale = 'en') {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

function formatDateRange(start, end, locale = 'en') {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (start === end) {
    return formatDateShort(start, locale);
  }
  return `${formatDateShort(start, locale)} - ${formatDateShort(end, locale)}`;
}

// Translate leave type name using i18n
function translateLeaveType(t, name) {
  if (!name) return '';
  const translated = t(`leave_type_name.${name}`);
  // If translation key doesn't exist, t() returns the key itself
  return translated.startsWith('leave_type_name.') ? name : translated;
}

// Get leave color with fallback and privacy awareness
function getLeaveColorDefault(leave) {
  if (leave.color) return leave.color;
  const defaults = {
    'Vacation': '#3B82F6',
    'Vacation Leave': '#3B82F6',
    'Sick': '#EF4444',
    'Sick Leave': '#EF4444',
    'Sick Leave (Full Pay)': '#EF4444',
    'Maternity Leave': '#EC4899',
    'Paternity Leave': '#06B6D4',
    'Bereavement Leave': '#6B7280',
  };
  return defaults[leave.leave_type || leave.leave_type_name] || '#6B7280';
}

// Privacy-aware leave color - hides paid/unpaid distinction from non-admins
function getLeaveColor(leave, viewerRole, viewerId) {
  const actualColor = getLeaveColorDefault(leave);
  const leaveTypeName = leave.leave_type || leave.leave_type_name || '';
  const isOwnLeave = leave.user_id === viewerId;
  return getPrivacyAwareLeaveColor(leaveTypeName, actualColor, viewerRole, isOwnLeave);
}

// Privacy-aware leave type name
function getLeaveTypeName(leave, viewerRole, viewerId) {
  const leaveTypeName = leave.leave_type || leave.leave_type_name || '';
  const isOwnLeave = leave.user_id === viewerId;
  return getPrivacyAwareLeaveTypeName(leaveTypeName, viewerRole, isOwnLeave);
}

export function Dashboard({ user, workspace, subscription, onLogout }) {
  const { hasFeature } = useAuth();
  const { t, locale } = useI18n();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [balances, setBalances] = useState([]);
  const [monthLeaves, setMonthLeaves] = useState([]);
  const [selectedDateLeaves, setSelectedDateLeaves] = useState([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fabOpen, setFabOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [nextHoliday, setNextHoliday] = useState(null);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [myNextVacation, setMyNextVacation] = useState(null);
  const fabRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [calendarMonth]);

  useEffect(() => {
    // Update selected date leaves when selectedDate changes
    const dateStr = formatLocalDate(selectedDate);
    const leaves = monthLeaves.filter(l =>
      dateStr >= l.start_date && dateStr <= l.end_date && l.status === 'approved'
    );
    setSelectedDateLeaves(leaves);
  }, [selectedDate, monthLeaves]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (fabRef.current && !fabRef.current.contains(e.target)) {
        setFabOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const today = new Date();
      const todayStr = formatLocalDate(today);
      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth();
      const firstDay = formatLocalDate(new Date(year, month, 1));
      const lastDay = formatLocalDate(new Date(year, month + 1, 0));

      // Also get next 7 days for upcoming
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = formatLocalDate(tomorrow);
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      const weekEndStr = formatLocalDate(weekEnd);

      const currentYear = today.getFullYear();
      const [statsData, balancesData, calendarData, upcomingData, holidaysData, nextYearHolidaysData, birthdaysData, myLeavesData] = await Promise.all([
        getDashboardStats(),
        getBalances(),
        getCalendarData(firstDay, lastDay),
        getCalendarData(todayStr, weekEndStr),
        getHolidays(currentYear),
        getHolidays(currentYear + 1),
        getUpcomingBirthdays().catch(() => []),
        getLeaves({ status: 'approved', start_date: todayStr }).catch(() => ({ leaves: [] }))
      ]);

      setStats(statsData);
      setBalances(balancesData);
      setMonthLeaves(calendarData.leaves || []);

      // Find next upcoming public holiday
      // API returns array directly. Normalize dates for recurring holidays
      const allHolidays = [
        ...(Array.isArray(holidaysData) ? holidaysData : []),
        ...(Array.isArray(nextYearHolidaysData) ? nextYearHolidaysData : [])
      ];

      // For each holiday, ensure we have an upcoming date (handle recurring with old years)
      const normalizedHolidays = allHolidays.map(h => {
        const monthDay = h.date.slice(5); // Get MM-DD
        let holidayDate = `${currentYear}-${monthDay}`;

        // If this year's date has passed, use next year
        if (holidayDate < todayStr) {
          holidayDate = `${currentYear + 1}-${monthDay}`;
        }

        return { ...h, normalizedDate: holidayDate };
      });

      // Filter to future dates, sort, and pick first
      const upcomingHoliday = normalizedHolidays
        .filter(h => h.normalizedDate >= todayStr)
        .sort((a, b) => a.normalizedDate.localeCompare(b.normalizedDate))[0];

      // Use normalized date for display
      if (upcomingHoliday) {
        setNextHoliday({ ...upcomingHoliday, date: upcomingHoliday.normalizedDate });
      } else {
        setNextHoliday(null);
      }

      // Set upcoming birthdays (API returns array directly)
      setUpcomingBirthdays(Array.isArray(birthdaysData) ? birthdaysData : []);

      // Filter upcoming leaves (next 7 days, approved only)
      // Include leaves that extend beyond today (end_date >= tomorrow)
      // This shows ongoing leaves that have future days, not just new leaves
      const upcoming = (upcomingData.leaves || [])
        .filter(l => l.status === 'approved' && l.end_date >= tomorrowStr)
        .sort((a, b) => a.start_date.localeCompare(b.start_date))
        .slice(0, 5);
      setUpcomingLeaves(upcoming);

      // Find my next booked vacation (current user's next approved leave, no date limit)
      const allMyLeaves = (myLeavesData.leaves || myLeavesData || []);
      const myNextLeave = allMyLeaves
        .filter(l => l.user_id === user?.id && l.start_date >= todayStr)
        .sort((a, b) => a.start_date.localeCompare(b.start_date))[0] || null;
      setMyNextVacation(myNextLeave);

      // Set initial selected date leaves
      const dateStr = formatLocalDate(selectedDate);
      const leaves = (calendarData.leaves || []).filter(l =>
        dateStr >= l.start_date && dateStr <= l.end_date && l.status === 'approved'
      );
      setSelectedDateLeaves(leaves);

    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }

  // Get primary balance for circular progress (usually Vacation)
  const primaryBalance = balances.find(b =>
    b.leave_type_name?.toLowerCase().includes('vacation') ||
    b.leave_type_name?.toLowerCase().includes('annual')
  ) || balances[0];

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  if (loading) {
    return (
      <div class="bento-loading">
        <div class="spinner" />
      </div>
    );
  }

  return (
    <>
      <div class="bento-grid">
        {/* Left Column */}
        <div class="mini-calendar-wrapper">
          <MiniCalendar
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            calendarMonth={calendarMonth}
            setCalendarMonth={setCalendarMonth}
            leaves={monthLeaves}
            t={t}
            locale={locale}
            userRole={user?.role}
            userId={user?.id}
          />

          <div class="bento-card out-today-card">
            <OutTodayCard
              leaves={selectedDateLeaves}
              selectedDate={selectedDate}
              isToday={isToday}
              t={t}
              userRole={user?.role}
              userId={user?.id}
            />
          </div>

          <div class="bento-card upcoming-card">
            <UpcomingAbsencesCard leaves={upcomingLeaves} t={t} />
          </div>
        </div>

        {/* Center Column - Leave List */}
        <div class="center-column-wrapper">
          <div class="bento-card leave-list-card">
            <LeaveListCard
              leaves={selectedDateLeaves}
              selectedDate={selectedDate}
              calendarMonth={calendarMonth}
              t={t}
              locale={locale}
              userRole={user?.role}
              userId={user?.id}
            />
          </div>
          <div class="bento-card all-balances-card horizontal">
            <AllBalancesCard balances={balances} t={t} />
          </div>
          <div class="bento-card birthdays-card">
            <UpcomingBirthdaysCard birthdays={upcomingBirthdays} t={t} />
          </div>
        </div>

        {/* Right Column */}
        <div class="balance-card-wrapper">
          <div class="bento-card balance-card">
            <BalanceCard balance={primaryBalance} t={t} />
          </div>
          <div class="bento-card next-holiday-card">
            <NextPublicHolidayCard holiday={nextHoliday} t={t} />
          </div>
          <div class="bento-card my-vacation-card">
            <MyNextVacationCard vacation={myNextVacation} t={t} />
          </div>
        </div>
      </div>

      <FloatingActionButton
        fabOpen={fabOpen}
        setFabOpen={setFabOpen}
        fabRef={fabRef}
        onContactSupport={() => setSupportModalOpen(true)}
        t={t}
      />

      {supportModalOpen && (
        <SupportModal onClose={() => setSupportModalOpen(false)} t={t} />
      )}
    </>
  );
}

// Top Navigation Component
function BentoTopNav({ user, workspace, userMenuOpen, setUserMenuOpen, userMenuRef, onLogout, mobileNavOpen, setMobileNavOpen }) {
  return (
    <nav class="bento-topnav">
      <div class="bento-topnav-left">
        <div class="bento-topnav-logo">U</div>
        <span class="bento-topnav-workspace">{workspace?.name || 'Workspace'}</span>
      </div>

      <div class="bento-topnav-center">
        <a href="/" class="bento-nav-link active">Dashboard</a>
        <a href="/wallchart" class="bento-nav-link">Planner</a>
        <a href="/requests" class="bento-nav-link">Requests</a>
        <a href="/calendar" class="bento-nav-link">Calendar</a>
      </div>

      <div class="bento-topnav-right">
        <button
          class="bento-mobile-menu-btn"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>

        <div class="header-dropdown" ref={userMenuRef} style={{ position: 'relative' }}>
          <button
            class="bento-user-btn"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div class="bento-user-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt="" />
              ) : (
                user?.name?.charAt(0) || 'U'
              )}
            </div>
            <span class="bento-user-name">{user?.name?.split(' ')[0] || 'User'}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {userMenuOpen && (
            <div class="bento-user-dropdown">
              <a href="/settings">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Settings
              </a>
              <a href="/billing">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Billing
              </a>
              <button class="danger" onClick={onLogout}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16,17 21,12 16,7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

// Helper to format date as YYYY-MM-DD in local timezone
function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Mini Calendar Component
function MiniCalendar({ selectedDate, onDateSelect, calendarMonth, setCalendarMonth, leaves, t, locale, userRole, userId }) {
  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();
  const today = new Date();
  const todayStr = toLocalDateStr(today);

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startDay = (firstDayOfMonth.getDay() + 6) % 7; // Monday = 0

  // Build days array
  const days = [];

  // Previous month days
  const prevMonth = new Date(year, month, 0);
  const prevMonthDays = prevMonth.getDate();
  for (let i = startDay - 1; i >= 0; i--) {
    const dayNum = prevMonthDays - i;
    const date = new Date(year, month - 1, dayNum);
    days.push({ num: dayNum, date, otherMonth: true });
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    const dateStr = toLocalDateStr(date);
    const hasLeave = leaves?.some(l => dateStr >= l.start_date && dateStr <= l.end_date && l.status === 'approved');
    const leaveForDay = hasLeave ? leaves.find(l => dateStr >= l.start_date && dateStr <= l.end_date) : null;
    const leaveColor = leaveForDay ? getLeaveColor(leaveForDay, userRole, userId) : null;
    days.push({
      num: i,
      date,
      isToday: dateStr === todayStr,
      isSelected: date.toDateString() === selectedDate.toDateString(),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      hasLeave,
      leaveColor
    });
  }

  // Next month days to fill grid
  const totalCells = Math.ceil(days.length / 7) * 7;
  const remainingDays = totalCells - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({ num: i, date, otherMonth: true });
  }

  // Use locale-aware month name
  const monthLabel = calendarMonth.toLocaleDateString(locale || 'en', { month: 'long', year: 'numeric' });

  // Weekday headers from translations
  const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  return (
    <div class="mini-calendar">
      <div class="mini-calendar-header">
        <span class="mini-calendar-title">{monthLabel}</span>
        <div class="mini-calendar-nav">
          <button onClick={() => setCalendarMonth(new Date(year, month - 1, 1))}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button onClick={() => setCalendarMonth(new Date(year, month + 1, 1))}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>

      <div class="mini-calendar-weekdays">
        {weekdayKeys.map((key, i) => (
          <div key={i} class="mini-calendar-weekday">{t(`weekday.${key}`).charAt(0)}</div>
        ))}
      </div>

      <div class="mini-calendar-grid">
        {days.map((day, i) => (
          <button
            key={i}
            class={`mini-calendar-day ${day.isToday ? 'today' : ''} ${day.isSelected && !day.isToday ? 'selected' : ''} ${day.otherMonth ? 'other-month' : ''} ${day.isWeekend && !day.otherMonth ? 'weekend' : ''}`}
            onClick={() => !day.otherMonth && onDateSelect(day.date)}
            disabled={day.otherMonth}
          >
            {day.num}
            {day.hasLeave && <span class="leave-dot" style={{ background: day.leaveColor }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

// Out Today Card Component
function OutTodayCard({ leaves, selectedDate, isToday, t, userRole, userId }) {
  const maxVisible = 5;
  const dateLabel = isToday
    ? t('dashboard.out_today')
    : t('dashboard.out_on_date', { date: selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) });

  return (
    <>
      <div class="out-today-header">
        <h3>{dateLabel}</h3>
        {leaves.length > 0 && <span class="out-today-count">{leaves.length}</span>}
      </div>
      {leaves.length > 0 ? (
        <div class="avatar-row">
          {leaves.slice(0, maxVisible).map((leave, i) => (
            <div
              key={i}
              class="avatar-item"
              title={`${leave.user_name} - ${getLeaveTypeName(leave, userRole, userId)}`}
            >
              {leave.avatar_url ? (
                <img src={leave.avatar_url} alt="" />
              ) : (
                leave.user_name?.charAt(0) || '?'
              )}
            </div>
          ))}
          {leaves.length > maxVisible && (
            <div class="avatar-overflow">+{leaves.length - maxVisible}</div>
          )}
        </div>
      ) : (
        <div class="no-absences">{t('dashboard.no_absences')}</div>
      )}
    </>
  );
}

// Leave List Card Component
function LeaveListCard({ leaves, selectedDate, calendarMonth, t, locale, userRole, userId }) {
  const monthLabel = calendarMonth.toLocaleDateString(locale || 'en', { month: 'long' });
  const yearLabel = calendarMonth.getFullYear();

  return (
    <>
      <div class="leave-list-header">
        <div>
          <div class="leave-list-header-title">{monthLabel}</div>
          <div class="leave-list-header-year">{yearLabel}</div>
        </div>
        <button class="add-btn" onClick={() => route('/requests')} title="View all requests">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>
      <div class="leave-list-scroll">
        {leaves.length > 0 ? (
          leaves.map((leave, i) => (
            <LeaveCard key={i} leave={leave} t={t} locale={locale} userRole={userRole} userId={userId} />
          ))
        ) : (
          <div class="bento-empty">
            <div class="bento-empty-icon">🎉</div>
            <h4>{t('dashboard.no_absences')}</h4>
            <p>{t('dashboard.everyone_in_office')}</p>
          </div>
        )}
      </div>
    </>
  );
}

// Individual Leave Card Component
function LeaveCard({ leave, t, locale, userRole, userId }) {
  const color = getLeaveColor(leave, userRole, userId);
  const gradient = getGradient(color);
  const leaveTypeName = translateLeaveType(t, getLeaveTypeName(leave, userRole, userId));

  return (
    <div class="leave-card" style={{ background: gradient, cursor: 'pointer' }} onClick={() => route('/wallchart')}>
      <div class="leave-card-content">
        <div class="avatar">
          {leave.avatar_url ? (
            <img src={leave.avatar_url} alt="" />
          ) : (
            leave.user_name?.charAt(0) || '?'
          )}
        </div>
        <div class="leave-card-info">
          <span class="leave-type">{leaveTypeName}</span>
          <span class="user-name">{leave.user_name}</span>
          <span class="dates">
            {(() => {
              const isPartialDay = leave.start_time && leave.end_time && leave.start_date === leave.end_date;
              if (isPartialDay) {
                return `${formatDateShort(leave.start_date, locale)} ${leave.start_time}-${leave.end_time}`;
              }
              return formatDateRange(leave.start_date, leave.end_date, locale);
            })()}
          </span>
        </div>
        <div class="leave-card-chevron">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// Balance Card with Fuel Gauge Style
function BalanceCard({ balance, t }) {
  if (!balance) {
    return (
      <>
        <h3>{t('dashboard.my_balance')}</h3>
        <div class="bento-empty">
          <p>{t('dashboard.no_balance_data')}</p>
        </div>
      </>
    );
  }

  const used = balance.used || 0;
  const entitlement = balance.entitlement || 0;
  const isUnlimited = entitlement === -1;
  const availableNum = balance.available || 0;
  // For gauge: show percentage of days REMAINING (available/entitlement)
  const percent = isUnlimited ? 100 : (entitlement > 0 ? Math.round((availableNum / entitlement) * 100) : 0);
  const color = balance.color || '#F97316';
  const isHours = balance.unit === 'hours';

  // Format available balance using shared formatter (handles hours, days, weeks, months)
  const availableDisplay = isUnlimited
    ? t('balance.unlimited')
    : formatLeaveValue(availableNum, balance.unit, t);
  const unitLabel = ''; // Unit is now included in formatLeaveValue output

  // SVG arc calculations for fuel gauge (270 degrees = 3/4 circle)
  const radius = 46;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75; // 270 degrees
  const gapLength = circumference * 0.25; // 90 degree gap at bottom
  const filledLength = isUnlimited ? arcLength : (percent / 100) * arcLength;

  return (
    <>
      <h3>{t('dashboard.my_balance')}</h3>
      <div class="fuel-gauge">
        <svg viewBox="0 0 120 120">
          {/* Background arc (gray) */}
          <circle
            class="gauge-background"
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            stroke-width={strokeWidth}
            stroke-dasharray={`${arcLength} ${gapLength}`}
            stroke-linecap="round"
            transform="rotate(135 60 60)"
          />
          {/* Progress arc (colored) */}
          <circle
            class="gauge-progress"
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={color}
            stroke-width={strokeWidth}
            stroke-dasharray={`${filledLength} ${circumference}`}
            stroke-linecap="round"
            transform="rotate(135 60 60)"
          />
        </svg>
        <div class="gauge-center">
          {isUnlimited ? (
            <>
              <span class="gauge-value">∞</span>
              <span class="gauge-label">{t('time.days')}</span>
            </>
          ) : isHours ? (
            <>
              {(() => {
                const { days, hours: hrs } = formatHoursCompact(availableNum);
                return (
                  <>
                    <span class="gauge-value">{days}</span>
                    <span class="gauge-label">
                      {t('time.days')}{hrs > 0 && ` +${hrs}h`}
                    </span>
                  </>
                );
              })()}
            </>
          ) : (
            <>
              <span class="gauge-value">{availableNum}</span>
              <span class="gauge-label">{getUnitLabel(balance.unit, t)} {t('balance.days_left').split(' ').slice(1).join(' ')}</span>
            </>
          )}
        </div>
      </div>
      {!isUnlimited && (
        <div class="balance-stats">
          <span><span class="dot used" style={{ background: '#9CA3AF' }} /> {formatLeaveValue(used, balance.unit, t)} {t('balance.used')}</span>
        </div>
      )}
    </>
  );
}

// All Balances Card - shows all leave type balances
function AllBalancesCard({ balances, t }) {
  if (!balances || balances.length === 0) {
    return (
      <>
        <div class="my-balances-header">{t('dashboard.my_balances')}</div>
        <div class="bento-empty">
          <p>{t('dashboard.no_balance_data')}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div class="my-balances-header">{t('dashboard.my_balances')}</div>
      <ul class="all-balances-list">
        {balances.map((balance, i) => {
          const used = balance.used || 0;
          const entitlement = balance.entitlement || 0;
          const isUnlimited = entitlement === -1;
          const availableNum = balance.available || 0;
          const isHours = balance.unit === 'hours';
          const color = balance.color || '#6B7280';
          const leaveTypeName = translateLeaveType(t, balance.leave_type_name);

          // Format using shared formatter (handles hours, days, weeks, months)
          const availableDisplay = isUnlimited ? '∞' : formatLeaveValue(availableNum, balance.unit, t);
          const usedDisplay = formatLeaveValue(used, balance.unit, t);
          const entitlementDisplay = formatLeaveValue(entitlement, balance.unit, t);

          return (
            <li key={i} class="all-balances-item">
              <div class="balance-color-dot" style={{ background: color }} />
              <div class="balance-type-info">
                <span class="balance-type-name">{leaveTypeName}</span>
                <span class="balance-type-detail">
                  {isUnlimited ? t('balance.unlimited') : `${usedDisplay} ${t('balance.used')} ${t('balance.of')} ${entitlementDisplay}`}
                </span>
              </div>
              <div class="balance-available">
                <span class="balance-available-value">{availableDisplay}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

// Next Public Holiday Card
function NextPublicHolidayCard({ holiday, t }) {
  if (!holiday) {
    return (
      <>
        <h3>{t('dashboard.next_holiday')}</h3>
        <div class="bento-empty">
          <p>{t('dashboard.no_holidays')}</p>
        </div>
      </>
    );
  }

  // Format the date nicely
  const holidayDate = new Date(holiday.date + 'T00:00:00');
  const dayName = holidayDate.toLocaleDateString('en-US', { weekday: 'long' });
  const monthDay = holidayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Calculate days until holiday
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = holidayDate - today;
  const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return (
    <>
      <h3>{t('dashboard.next_holiday')}</h3>
      <div class="next-holiday-content">
        <div class="holiday-icon">🎉</div>
        <div class="holiday-info">
          <span class="holiday-name">{holiday.name}</span>
          <span class="holiday-date">{dayName}, {monthDay}</span>
        </div>
        <div class="holiday-countdown">
          <span class="countdown-value">{daysUntil}</span>
          <span class="countdown-label">{daysUntil === 1 ? t('time.day') : t('time.days')}</span>
        </div>
      </div>
    </>
  );
}

// My Next Vacation Card
function MyNextVacationCard({ vacation, t }) {
  if (!vacation) {
    return (
      <>
        <h3>{t('dashboard.next_vacation')}</h3>
        <div class="no-vacation-booked">
          <svg class="vacation-cartoon" viewBox="0 0 120 100" fill="none">
            {/* Simple cartoon character */}
            <circle cx="60" cy="35" r="20" fill="#FCD34D" /> {/* Head */}
            <circle cx="53" cy="32" r="3" fill="#1F2937" /> {/* Left eye */}
            <circle cx="67" cy="32" r="3" fill="#1F2937" /> {/* Right eye */}
            <path d="M52 42 Q60 48 68 42" stroke="#1F2937" stroke-width="2" fill="none" stroke-linecap="round" /> {/* Smile */}
            <rect x="50" y="55" width="20" height="25" rx="3" fill="#3B82F6" /> {/* Body */}
            <rect x="42" y="58" width="8" height="4" rx="2" fill="#3B82F6" /> {/* Left arm */}
            <rect x="70" y="58" width="8" height="4" rx="2" fill="#3B82F6" /> {/* Right arm */}
            <text x="60" y="95" text-anchor="middle" fill="#6B7280" font-size="10">{t('dashboard.nothing_booked')}</text>
          </svg>
        </div>
      </>
    );
  }

  // Format the dates
  const startDate = new Date(vacation.start_date + 'T00:00:00');
  const endDate = new Date(vacation.end_date + 'T00:00:00');
  const startFormatted = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endFormatted = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const dateRange = vacation.start_date === vacation.end_date
    ? startFormatted
    : `${startFormatted} - ${endFormatted}`;

  // Calculate days until
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = startDate - today;
  const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return (
    <>
      <h3>{t('dashboard.next_vacation')}</h3>
      <div class="my-vacation-content">
        <div class="vacation-icon">🏖️</div>
        <div class="vacation-info">
          <span class="vacation-type">{vacation.leave_type || vacation.leave_type_name}</span>
          <span class="vacation-dates">{dateRange}</span>
        </div>
        <div class="vacation-countdown">
          <span class="countdown-value">{daysUntil}</span>
          <span class="countdown-label">{daysUntil === 1 ? t('time.day') : t('time.days')}</span>
        </div>
      </div>
    </>
  );
}

// Upcoming Absences Card
function UpcomingAbsencesCard({ leaves, t }) {
  // Format time like "8am" or "12pm"
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const displayHour = hours % 12 || 12;
    return minutes > 0 ? `${displayHour}:${String(minutes).padStart(2, '0')}${period}` : `${displayHour}${period}`;
  };

  // Format duration display
  const formatDuration = (leave) => {
    const unit = leave.leave_type_unit || 'days';
    const duration = leave.duration_days;

    // Hours-based leave
    if (unit === 'hours') {
      // Full day (8 hours) - just show "Full day"
      if (duration >= 8) {
        return 'Full day';
      }
      // Partial hours with times - show time range
      if (leave.start_time && leave.end_time) {
        return `${formatTime(leave.start_time)} - ${formatTime(leave.end_time)}`;
      }
      return `${duration}h`;
    }

    // Days-based leave
    if (duration > 1) {
      return `${duration} days`;
    }

    if (duration === 1) {
      return 'Full day';
    }

    // Half day with times - show time range
    if (duration === 0.5 && leave.start_time && leave.end_time) {
      return `${formatTime(leave.start_time)} - ${formatTime(leave.end_time)}`;
    }

    if (duration === 0.5) {
      return 'Half day';
    }

    return 'Full day';
  };

  return (
    <>
      <h3>{t('dashboard.upcoming')}</h3>
      {leaves.length > 0 ? (
        <ul class="upcoming-list">
          {leaves.map((leave, i) => (
            <li key={i} class="upcoming-item" onClick={() => route('/wallchart')}>
              <div class="avatar-small">
                {leave.avatar_url ? (
                  <img src={leave.avatar_url} alt="" />
                ) : (
                  leave.user_name?.charAt(0) || '?'
                )}
              </div>
              <div class="upcoming-info">
                <span class="upcoming-name">{leave.user_name}</span>
                <span class="upcoming-date">
                  {(() => {
                    const startDate = formatDateShort(leave.start_date);
                    const endDate = formatDateShort(leave.end_date);
                    const isPartialDay = leave.start_time && leave.end_time && leave.start_date === leave.end_date;
                    const isSingleDay = leave.start_date === leave.end_date;

                    if (isPartialDay) {
                      // Partial day: "29 Jan 10:30-14:00"
                      return `${startDate} ${leave.start_time}-${leave.end_time}`;
                    } else if (isSingleDay) {
                      // Single full day: "29 Jan"
                      return startDate;
                    } else {
                      // Multi-day: "1 Dec - 5 Apr"
                      return `${startDate} - ${endDate}`;
                    }
                  })()}
                </span>
              </div>
              <div class="upcoming-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div class="no-upcoming">{t('dashboard.upcoming_empty')}</div>
      )}
    </>
  );
}

// Upcoming Birthdays Card
function UpcomingBirthdaysCard({ birthdays, t }) {
  if (!birthdays || birthdays.length === 0) {
    return (
      <>
        <h3>🎂 {t('dashboard.upcoming_birthdays')}</h3>
        <div class="no-upcoming">{t('dashboard.no_birthdays')}</div>
      </>
    );
  }

  return (
    <>
      <h3>🎂 {t('dashboard.upcoming_birthdays')}</h3>
      <ul class="birthdays-list">
        {birthdays.map((person, i) => {
          const birthdayDate = new Date(person.upcoming_date + 'T00:00:00');
          const monthDay = birthdayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

          return (
            <li key={i} class="birthday-item">
              <div class="avatar-small">
                {person.avatar_url ? (
                  <img src={person.avatar_url} alt="" />
                ) : (
                  person.name?.charAt(0) || '?'
                )}
              </div>
              <div class="birthday-info">
                <span class="birthday-name">{person.name}</span>
                <span class="birthday-date">{monthDay}</span>
              </div>
              <div class="birthday-countdown">
                {person.days_until === 0 ? (
                  <span class="birthday-today">🎉 {t('dashboard.birthday_today')}</span>
                ) : (
                  <span class="birthday-days">{t('dashboard.birthday_in_days', { days: person.days_until, unit: person.days_until === 1 ? t('time.day') : t('time.days') })}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

// Floating Action Button
function FloatingActionButton({ fabOpen, setFabOpen, fabRef, onContactSupport, t }) {
  return (
    <div class="fab-container" ref={fabRef}>
      <button class={`fab ${fabOpen ? 'open' : ''}`} onClick={() => setFabOpen(!fabOpen)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
      {fabOpen && (
        <div class="fab-menu">
          <button class="fab-menu-item" onClick={() => route('/requests')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {t('dashboard.request_leave')}
          </button>
          <button class="fab-menu-item" onClick={() => route('/calendar')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {t('dashboard.view_calendar')}
          </button>
          <button class="fab-menu-item" onClick={() => route('/wallchart')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
            {t('dashboard.view_planner')}
          </button>
          <button class="fab-menu-item" onClick={() => { setFabOpen(false); onContactSupport(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            {t('dashboard.contact_support')}
          </button>
        </div>
      )}
    </div>
  );
}

// Support Modal Component
const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

function SupportModal({ onClose, t }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const sessionId = localStorage.getItem('sessionId');
      const res = await fetch(`${API_URL}/v3/support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({ subject, message })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSuccess(true);
        setTimeout(() => onClose(), 3000);
      } else {
        setError(data.error || 'Failed to send support request');
      }
    } catch (err) {
      setError('Failed to send support request. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div class="modal-overlay" onClick={onClose}>
      <div class="modal support-modal" onClick={e => e.stopPropagation()}>
        <div class="modal-header">
          <h2>{t('support.title')}</h2>
          <button class="modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          {success ? (
            <div class="success-message">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <h3>{t('support.success_title')}</h3>
              <p>{t('support.success_message')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <p class="support-intro">
                {t('support.intro')}
              </p>

              {error && (
                <div class="alert alert-error">{error}</div>
              )}

              <div class="form-group">
                <label for="support-subject">{t('support.subject_label')}</label>
                <input
                  type="text"
                  id="support-subject"
                  value={subject}
                  onInput={e => setSubject(e.target.value)}
                  placeholder={t('support.subject_placeholder')}
                  required
                />
              </div>

              <div class="form-group">
                <label for="support-message">{t('support.message_label')}</label>
                <textarea
                  id="support-message"
                  value={message}
                  onInput={e => setMessage(e.target.value)}
                  placeholder={t('support.message_placeholder')}
                  rows="5"
                  required
                />
              </div>

              <div class="modal-actions">
                <button type="button" class="btn btn-secondary" onClick={onClose}>
                  {t('common.cancel')}
                </button>
                <button type="submit" class="btn btn-primary" disabled={loading}>
                  {loading ? t('common.sending') : t('support.send_button')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
